import { capitalize } from 'lodash';
import { translate } from '@/utils/translations';
import {
  AppDomainsAggregatesModelsOrderByVariable,
  GetInteractionAggregatesReq,
  GetInteractionAggregatesReqGroupBy,
  GetInteractionsReq,
  QueryOrdering,
} from '@/api/generated/model';
import { useMemo } from 'react';
import { mapFilterKindToAPIInteractionAggregatesGroupBy } from '@/models/interaction-aggregates/mappers/map-to-api';
import { AllFilterValue, FILTERS } from '@/models/common/filters';
import { isSpecialFilter, splitFilters } from '@/utils/filter-utils';
import { getTimeRangeConfigDefault } from '@/utils/time-range-config';
import { useFetchInteractionAggregates } from '@/models/interaction-aggregates/useFetchInteractionAggregates';
import { InteractionAggregates } from '@/models/interaction-aggregates/internal-models';
import { StarRating } from '@/domains/home/components/home-tabs/_components/sheets/home-page-interactions-sidebar-item';
import {
  AvailableValue,
  FilterButtonProps,
  GenericFilter,
  SpecialFilter,
  VolumeKind,
} from '@/comps/FilterPicker/FilterPicker';
import { AggregateBy } from '@/domains/dashboard/components/GlobalfiltersRow';
import { humanizeString } from '@/utils/string.utils';
import { KindAliasFn } from '@/contexts/alias';
import { groupByToFilter } from '@/contexts/userIntelligence/fetchers';
import { useQuery } from '@tanstack/react-query';
import { getAvailableConversationLabels } from '@/api/generated/conversations/conversations';
import { getAvailableInteractionLabels, getInteractions } from '@/api/generated/interactions/interactions';

export const SAVE_FILTERS_REQUIRES_WRITE_MESSAGE = 'You need edit permissions to save filters. Ask an admin.';

export function buildFilterKindName(kind: string, alias?: KindAliasFn) {
  const aliasedName = alias?.(kind);
  if (aliasedName) return aliasedName;
  if (kind === FILTERS.kindInteractionId) return 'Interaction ID';
  return translate('filter', kind) === kind ? humanizeString(kind) : translate('filter', kind);
}

export function sortFilterKindName(a: string, b: string, alias?: KindAliasFn) {
  const itemNameA = buildFilterKindName(a, alias);
  const itemNameB = buildFilterKindName(b, alias);

  if (itemNameA > itemNameB) return 1;
  if (itemNameA < itemNameB) return -1;
  return 0;
}

export function sortFilterKindsWithFavoritesFirst(kinds: string[], favoriteTypes: Set<string>) {
  return [...kinds].sort((a, b) => {
    const aFav = favoriteTypes.has(a);
    const bFav = favoriteTypes.has(b);
    if (aFav !== bFav) return aFav ? -1 : 1;
    return sortFilterKindName(a, b);
  });
}

type GetFilterValuesParams<T extends GenericFilter> = {
  filters: FilterButtonProps<T>['filters'];
  filters_v2?: FilterButtonProps<T>['filters_v2'];
  timeRange: FilterButtonProps<T>['timeRange'];
  projectId: FilterButtonProps<T>['projectId'];
  additionalFilters?: FilterButtonProps<T>['additionalFilters'];
  overrideSelectedFilters?: FilterButtonProps<T>['overrideSelectedFilters'];
  filterKind: string | null;
  search: string;
  volumeKind: VolumeKind | undefined;
  filtersMapByKind: Record<string, string[]>;
  isSelectedAll?: boolean;
  sortBy: QueryOrdering;
  enabled?: boolean;
  aggregateBy: AggregateBy;
};

export function useGetFilterValues<T extends GenericFilter>({ enabled = true, ...params }: GetFilterValuesParams<T>) {
  const {
    filters,
    filters_v2,
    filterKind,
    timeRange,
    projectId,
    search,
    additionalFilters,
    filtersMapByKind,
    isSelectedAll,
    volumeKind: _volumeKind,
    sortBy,
    aggregateBy,
  } = params;
  // in the case of user interaction position, we need to handle it differently
  // because at a moment is not saved in the database as a filter,
  // but rather as a special filter with values 'first' and 'last'
  // which we return it hardcoding them
  const isUserPositionKind = filterKind === FILTERS.columnUserInteractionPosition;
  const isInteractionIdFilterKind = filterKind === FILTERS.kindInteractionId;

  const { regularFilters, specialFilters: splitSpecialFilters } = useMemo(() => splitFilters(filters), [filters]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const specialFilters = splitSpecialFilters as any as SpecialFilter[];

  const groupBy: GetInteractionAggregatesReqGroupBy | undefined = useMemo(() => {
    return mapFilterKindToAPIInteractionAggregatesGroupBy(filterKind as AllFilterValue | null, aggregateBy);
  }, [aggregateBy, filterKind]);

  const isLabelFilterKind = useMemo(
    () => filterKind === FILTERS.kindInteractionLabel || filterKind === FILTERS.kindConversationLabel,
    [filterKind],
  );

  const payload = useMemo(() => {
    return {
      filters: [
        ...regularFilters.filter((e) => e.column !== filterKind).concat(additionalFilters ?? []),
        ...(groupBy && search
          ? [{ column: groupByToFilter(groupBy), values: [], search: search } as GenericFilter]
          : []),
      ],
      filters_v2: filters_v2,
      special_filters: specialFilters,
      project_id: projectId,
      time_range: timeRange ?? getTimeRangeConfigDefault(),
      group_by: groupBy,
      limit: 100,
      variables: ['n_interactions', 'n_users'], // TODO: should this use "aggregateBy" to pick interactions or conversations variants?
      order_by: {
        by: sortBy.by,
        direction: sortBy.direction,
      },
    } as GetInteractionAggregatesReq;
  }, [
    additionalFilters,
    projectId,
    timeRange,
    filters_v2,
    groupBy,
    sortBy.by,
    sortBy.direction,
    search,
    filterKind,
    specialFilters,
    regularFilters,
  ]);

  const interactionIdPayload = useMemo(() => {
    return {
      filters: [
        ...regularFilters.filter((e) => e.column !== filterKind).concat(additionalFilters ?? []),
        {
          column: FILTERS.columnInteractionId,
          values: [],
          ...(search ? { search } : {}),
        },
      ],
      filters_v2: filters_v2,
      project_id: projectId,
      time_range: timeRange ?? getTimeRangeConfigDefault(),
      limit: 100,
      offset: 0,
      order_by: {
        by: 'timestamp',
        direction: 'desc',
      },
    } as GetInteractionsReq;
  }, [additionalFilters, filterKind, filters_v2, projectId, regularFilters, search, timeRange]);

  const { data, isLoading } = useFetchInteractionAggregates({
    payload,
    aggregateBy,
    enabled:
      enabled &&
      !!projectId &&
      !!filterKind &&
      !isUserPositionKind &&
      !isInteractionIdFilterKind &&
      !isSpecialFilter(filterKind),
  });

  const { data: interactionIdData, isLoading: isLoadingInteractionIds } = useQuery({
    queryKey: ['interactionIdFilterValues', interactionIdPayload, enabled],
    queryFn: ({ signal }) => getInteractions(interactionIdPayload, { signal }),
    enabled: enabled && !!projectId && isInteractionIdFilterKind,
  });

  const { data: availableLabelsData, isLoading: isLoadingLabels } = useQuery({
    queryKey: ['availableLabelsForFilter', projectId, filterKind],
    queryFn: ({ signal }) => {
      if (filterKind === FILTERS.kindConversationLabel) {
        return getAvailableConversationLabels({ project_id: projectId }, { signal });
      }
      return getAvailableInteractionLabels({ project_id: projectId }, { signal });
    },
    enabled: enabled && !!projectId && isLabelFilterKind,
  });

  const variable: keyof InteractionAggregates = 'nInteractions';
  const availableFilterValues = useMemo(() => {
    if (isLabelFilterKind) {
      const selectedValues = filtersMapByKind[filterKind ?? ''] ?? [];
      const labels = Array.from(
        new Set((availableLabelsData?.labels ?? []).map((label) => label.trim()).filter(Boolean)),
      );
      const searchLower = search.toLowerCase().trim();
      const volumeByLabel = new Map(
        (data?.aggregates ?? []).map((aggregate) => [aggregate.groupName, aggregate.nInteractions] as const),
      );

      const filtered = labels.filter((label) => label.toLowerCase().includes(searchLower));
      const selectedMissing = selectedValues.filter((value) => !labels.includes(value));
      const searchAsNew =
        searchLower.length > 0 &&
        !labels.some((label) => label.toLowerCase() === searchLower) &&
        !selectedValues.some((value) => value.toLowerCase() === searchLower)
          ? [search.trim()]
          : [];

      const mergedValues = Array.from(new Set([...searchAsNew, ...selectedMissing, ...filtered]));
      return mergedValues.map((value) => ({ value, volume: volumeByLabel.get(value) })) as AvailableValue[];
    }

    if (isUserPositionKind) {
      return [{ value: 'first' }, { value: 'last' }];
    }

    if (isInteractionIdFilterKind) {
      const selected = new Set(filtersMapByKind[filterKind ?? ''] ?? []);
      const values = Array.from(new Set((interactionIdData?.data ?? []).map((interaction) => interaction.id)));

      return values
        .filter((value) => !selected.has(value))
        .map((value) => ({ value }) as AvailableValue)
        .concat(
          (filtersMapByKind[filterKind ?? ''] ?? []).map((value) => {
            return { value } as AvailableValue;
          }),
        );
    }

    const selected = new Set(filtersMapByKind[filterKind ?? ''] ?? []);

    return data?.aggregates
      .filter((aggregate) => aggregate.groupName.length)
      .filter((aggregate) => !(filtersMapByKind[filterKind ?? ''] ?? []).includes(aggregate.groupName))
      .map((aggregate) => {
        return { value: aggregate.groupName, volume: aggregate[variable] } as AvailableValue;
      })
      .concat(
        filtersMapByKind[filterKind ?? '']?.map((groupName) => {
          return { value: groupName, volume: data?.aggregates.find((e) => e.groupName === groupName)?.[variable] };
        }) ?? ([] as AvailableValue[]),
      )
      .sort((a, b) => {
        const aIsOther = a.value.toLowerCase() === 'other';
        const bIsOther = b.value.toLowerCase() === 'other';
        if (aIsOther !== bIsOther) return aIsOther ? 1 : -1;

        const aSelected = selected.has(a.value) || isSelectedAll;
        const bSelected = selected.has(b.value) || isSelectedAll;
        if (aSelected !== bSelected) return aSelected ? -1 : 1;

        return (
          data.aggregates.findIndex((agg) => agg.groupName === a.value) -
          data.aggregates.findIndex((agg) => agg.groupName === b.value)
        );
      });
  }, [
    availableLabelsData?.labels,
    data?.aggregates,
    filterKind,
    filtersMapByKind,
    interactionIdData?.data,
    isInteractionIdFilterKind,
    isLabelFilterKind,
    isSelectedAll,
    isUserPositionKind,
    search,
  ]);

  return {
    data,
    isLoading: isUserPositionKind
      ? false
      : isInteractionIdFilterKind
        ? isLoadingInteractionIds
        : isLabelFilterKind
          ? isLoadingLabels
          : isLoading,
    availableFilterValues,
  };
}

export function formatValue(filterKind: string, value: string): string | React.ReactNode {
  if (['user', 'data_source'].includes(filterKind)) return value;

  if (filterKind === FILTERS.kindUserRating) {
    return StarRating({ rating: value });
  }

  // eslint-disable-next-line
  return capitalize(translate(filterKind as any, value));
}

export function getVariable(): AppDomainsAggregatesModelsOrderByVariable {
  return AppDomainsAggregatesModelsOrderByVariable.n_interactions;
}

export function formatFilter(filter: GenericFilter, alias?: KindAliasFn) {
  const kindName = alias?.(filter.column) ?? capitalize(translate('filter', filter.column));
  return `${kindName} is ${filter.values.map((v) => capitalize(v)).join(' or ')}`;
}

export function removeCohortFilter(f: GenericFilter) {
  return f.column !== FILTERS.columnCohort;
}

export function removeChartOnlyFilters(f: GenericFilter) {
  return (
    f.column !== FILTERS.firstInteraction &&
    f.column !== FILTERS.lastInteraction &&
    f.column !== FILTERS.usersInteraction
  );
}
